import { Dropdown, Field, Link as FluentLink, Option } from '@fluentui/react-components';
import CalendarDatePicker from '@shared/components/CalendarDatePicker';
import { Info20Filled, Warning20Filled } from '@fluentui/react-icons';
import { MultiUserSelect } from '@modules/users';
import { IconLabel } from '@shared/components';
import {
  EPeriodicReviewIntervalMonths,
  TPeriodicReviewSavingField,
  TPeriodicReviewSettingsValues,
} from '../../types/periodicReviews.types';
import { ymdToLocalDate } from '@shared/utils/date';

type TProps = {
  values: TPeriodicReviewSettingsValues;
  canEdit: boolean;
  canComplete: boolean;
  approvedVersionId?: string;
  savingField: TPeriodicReviewSavingField;
  onIntervalChange: (selectedValue?: string) => void | Promise<void>;
  onDateChange: (date?: Date | null) => void | Promise<void>;
  onResponsibleUserChange: (responsibleUser: string | null) => void | Promise<void>;
  onViewVersion: (versionId: string) => void;
};

const intervalOptions = [
  EPeriodicReviewIntervalMonths.SIX,
  EPeriodicReviewIntervalMonths.TWELVE,
  EPeriodicReviewIntervalMonths.TWENTY_FOUR,
  EPeriodicReviewIntervalMonths.THIRTY_SIX,
  EPeriodicReviewIntervalMonths.FORTY_EIGHT,
];

const PeriodicReviewSettings = ({
  values,
  canEdit,
  canComplete,
  approvedVersionId,
  savingField,
  onIntervalChange,
  onDateChange,
  onResponsibleUserChange,
  onViewVersion,
}: TProps) => (
  <div className="flex flex-col gap-4">
    <Field label="Review interval">
      <Dropdown
        selectedOptions={[String(values.reviewIntervalMonths)]}
        value={`${values.reviewIntervalMonths} months`}
        onOptionSelect={(_, data) => onIntervalChange(data.optionValue)}
        disabled={!canEdit || savingField === 'reviewIntervalMonths'}
      >
        {intervalOptions.map((interval) => (
          <Option key={interval} value={String(interval)} text={`${interval} months`}>
            {interval} months
          </Option>
        ))}
      </Dropdown>
    </Field>

    <Field label="Next review date">
      <CalendarDatePicker
        value={ymdToLocalDate(values.nextReviewDate)}
        onSelectDate={onDateChange}
        disabled={!canEdit || savingField === 'nextReviewDate'}
        placeholder="Pick a date"
      />
    </Field>

    <Field label="Responsible reviewer">
      <MultiUserSelect
        selectedUserIds={values.responsibleUser}
        setSelectedUserId={onResponsibleUserChange}
        disabled={!canEdit || savingField === 'responsibleUser'}
      />
    </Field>

    {!canEdit && (
      <IconLabel icon={<Info20Filled />} className="pt-2">
        You can inspect periodic review settings but cannot edit them.
      </IconLabel>
    )}

    {canComplete &&
      (approvedVersionId ? (
        <IconLabel icon={<Info20Filled />}>
          <span>
            This review applies to approved version{' '}
            <FluentLink onClick={() => onViewVersion(approvedVersionId)}>{approvedVersionId}</FluentLink>.
          </span>
        </IconLabel>
      ) : (
        <IconLabel icon={<Warning20Filled />}>
          This item has no approved version. Approve a version before performing a periodic review.
        </IconLabel>
      ))}
  </div>
);

export default PeriodicReviewSettings;
